// Background-image processing. A user-chosen image is validated, downscaled, and re-encoded to WebP IN THE
// BROWSER before we ever store it — so a 10000×10000 / 120MB PNG can't tank the app: it's either rejected
// (too big to even decode safely) or shrunk to a small WebP. Output is a data URI stored in the theme.

/** Reject inputs larger than this before decoding (a decode of a huge file can spike memory). */
import { t } from "@lingui/core/macro";
import { base64ToBytes, bytesToBase64, dataUriPayload } from "./base64";
import { invoke, isRemote, type CommandArgs } from "./rpc";
import { errText } from "./coerce";
import { imageGet, imageSet, imgSrc } from "./api";

const MAX_INPUT_BYTES = 10 * 1024 * 1024; // 10 MB
/** An animation's source may be bigger: it is decoded a frame at a time, never whole. */
const MAX_ANIMATED_INPUT_BYTES = 25 * 1024 * 1024;
/** Longest-side cap for the stored background — plenty for a full-screen backdrop, tiny once WebP-encoded. */
const MAX_DIM = 1920;
const WEBP_QUALITY = 0.82;
/** Cap on an animated background AFTER its re-encode; MAX_BYTES in bg_anim.rs, which steps the quality down
 *  to fit it. Backgrounds live in the image store and load on demand, so this bounds disk and backup size,
 *  not unlock time. */
export const MAX_ANIMATED_BYTES = 7 * 1024 * 1024;

/** A theme names a stored background as `bgstore:<key>`; the bytes are in the image store under
 *  `theme_bg_<content hash>`, shared by every theme that uses the same image. The theme row stays a few KB
 *  however big its images, and the webview fetches one over aicc-img:// only when a view showing it is on
 *  screen. A data: URI still works everywhere (themes saved before this); theme.migrateThemeBackgrounds
 *  moves those over. Rust's theme_bg_sweep deletes files no theme names. */
const BG_REF = "bgstore:";
export const bgStoreKey = (v: string): string | null => (v.startsWith(BG_REF) ? v.slice(BG_REF.length) : null);
/** What a CSS url() or <img> loads for a stored or inline background. */
export const bgUrl = (v: string): string => { const k = bgStoreKey(v); return k ? imgSrc(k) : v; };

/** Store a background's data URI and return its reference. Content-addressed, so storing it twice is free. */
export async function storeBackground(dataUri: string): Promise<string> {
  if (bgStoreKey(dataUri)) return dataUri;
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-256", dataUriToBytes(dataUri) as BufferSource));
  const key = "theme_bg_" + [...hash.slice(0, 12)].map((b) => b.toString(16).padStart(2, "0")).join("");
  await imageSet(key, dataUri);
  return BG_REF + key;
}

/** A background as a data URI again, for a .rctheme export. */
export async function inlineBackground(v: string): Promise<string> {
  const k = bgStoreKey(v);
  if (!k) return v;
  const uri = await imageGet(k, "image/webp");
  if (!uri) throw new Error(t`One of this theme's background images is missing. Set it again, then export.`);
  return uri;
}

/** True for an animated WebP: RIFF....WEBP with a VP8X chunk whose animation flag (0x02) is set. */
export function isAnimatedWebp(b: Uint8Array): boolean {
  const tag = (at: number) => String.fromCharCode(b[at], b[at + 1], b[at + 2], b[at + 3]);
  return b.length > 20 && tag(0) === "RIFF" && tag(8) === "WEBP" && tag(12) === "VP8X" && (b[20] & 0x02) !== 0;
}

export interface ProcessedImage {
  dataUri: string;
  width: number;
  height: number;
  bytes: number; // approximate encoded size
}

/** Validate + downscale + WebP-encode a picked image file. Throws a user-readable Error on bad/oversized input. */
// Blob, not File: an imported .rctheme has raw bytes, and those need the same downscale a picked file
// gets — see zipToPack. Every field used below (type, size) is on Blob.
export async function processBackgroundImage(file: Blob): Promise<ProcessedImage> {
  if (isAnimatedWebp(new Uint8Array(await file.slice(0, 32).arrayBuffer()))) return processAnimatedWebp(file);
  const out = await processImage(file, { maxDim: MAX_DIM, quality: WEBP_QUALITY, maxInputBytes: MAX_INPUT_BYTES });
  return { dataUri: await blobToDataUri(out.blob), width: out.width, height: out.height, bytes: out.blob.size };
}

/** An animated WebP goes through the same downscale and re-encode as a still, but in Rust (bg_anim.rs): a
 *  canvas re-encode keeps only the first frame. Desktop only, since the remote relay carries JSON. */
async function processAnimatedWebp(file: Blob): Promise<ProcessedImage> {
  if (file.size > MAX_ANIMATED_INPUT_BYTES) {
    const mb = (file.size / 1048576).toFixed(0);
    throw new Error(t`Image is too large (${mb} MB). Please pick one under ${Math.round(MAX_ANIMATED_INPUT_BYTES / 1048576)} MB.`);
  }
  if (isRemote()) throw new Error(t`Set animated backgrounds on the desktop app.`);
  let out: Uint8Array;
  try {
    // A Uint8Array as the whole argument is sent as a raw IPC body, not JSON (the command reads it raw).
    out = new Uint8Array(await invoke<ArrayBuffer>("bg_anim_reencode", new Uint8Array(await file.arrayBuffer()) as unknown as CommandArgs));
  } catch (e) {
    throw new Error(errText(e));
  }
  if (out.length > MAX_ANIMATED_BYTES) {
    const mb = (out.length / 1048576).toFixed(1);
    throw new Error(t`Animated backgrounds can be up to 7 MB once converted, and this one is ${mb} MB. Try a shorter or smaller animation.`);
  }
  const blob = new Blob([out as BlobPart], { type: "image/webp" });
  const bmp = await createImageBitmap(blob);
  const { width, height } = bmp;
  bmp.close?.();
  return { dataUri: await blobToDataUri(blob), width, height, bytes: out.length };
}

/** The shared decode → downscale → WebP step (chat backgrounds above, card images in cardImages.ts, the
 *  Discord bot's picture). `scaled` says whether the image was shrunk; callers that may keep the original
 *  need to know. `square` crops to the largest square first, from the TOP of a portrait so a character's
 *  face stays in; `type` picks another encoding than WebP. */
export async function processImage(file: Blob, opts: { maxDim: number; quality: number; maxInputBytes: number; square?: boolean; type?: string }): Promise<{ blob: Blob; width: number; height: number; scaled: boolean }> {
  if (!file.type.startsWith("image/")) throw new Error(t`That file isn't an image.`);
  if (file.size > opts.maxInputBytes) {
    const mb = (file.size / 1048576).toFixed(0);
    const limit = Math.round(opts.maxInputBytes / 1048576);
    throw new Error(t`Image is too large (${mb} MB). Please pick one under ${limit} MB.`);
  }
  // createImageBitmap decodes off the main thread and throws on anything that isn't a real image → our type gate.
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file);
  } catch {
    throw new Error(t`Couldn't read that image — it may be corrupt or an unsupported format.`);
  }
  try {
    const side = Math.min(bmp.width, bmp.height);
    const src = opts.square
      ? { x: (bmp.width - side) / 2, y: bmp.height > bmp.width ? 0 : (bmp.height - side) / 2, w: side, h: side }
      : { x: 0, y: 0, w: bmp.width, h: bmp.height };
    const scale = Math.min(1, opts.maxDim / Math.max(src.w, src.h));
    const w = Math.max(1, Math.round(src.w * scale));
    const h = Math.max(1, Math.round(src.h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error(t`Couldn't process the image (no canvas context).`);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bmp, src.x, src.y, src.w, src.h, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, opts.type ?? "image/webp", opts.quality));
    if (!blob) throw new Error(t`Couldn't encode the image.`);
    return { blob, width: w, height: h, scaled: scale < 1 };
  } finally {
    bmp.close?.();
  }
}

export function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error ?? new Error("read failed"));
    fr.readAsDataURL(blob);
  });
}

/** data:*;base64,XXXX → raw bytes (for zipping into a .rctheme). */
export const dataUriToBytes = (uri: string): Uint8Array => base64ToBytes(dataUriPayload(uri));

/** Raw WebP bytes → a data URI (reading a .rctheme's images back in). A theme background is megabytes,
 *  which is why this goes through the chunked encoder rather than a byte-at-a-time loop. */
export const bytesToDataUri = (bytes: Uint8Array, mime = "image/webp"): string =>
  `data:${mime};base64,${bytesToBase64(bytes)}`;
